import { StoreDownloadItem, StoreDownloadSource } from 'types/storefront';

export type DownloadAccess = 'public' | 'loggedIn';

export type DownloadCustomerAudience = {
  priceListId?: string | null;
  zoneId?: string | null;
  sellerId?: string | null;
};

export const DOWNLOAD_ACCESS_OPTIONS: Array<{ value: DownloadAccess; label: string; helper: string }> = [
  {
    value: 'public',
    label: 'Acceso público',
    helper: 'Cualquier visitante puede descargar este archivo o abrir el enlace.',
  },
  {
    value: 'loggedIn',
    label: 'Solo usuarios logueados',
    helper:
      'Solo clientes con sesión iniciada. Podés acotar por lista de precios, zona y vendedor; si no elegís ninguno, aplica a todos los logueados. El enlace no se expone a visitantes anónimos.',
  },
];

export const DOWNLOAD_SECTION_DEFAULTS = {
  title: 'Descargas',
  subtitle: '',
  items: [] as StoreDownloadItem[],
};

export const parseDownloadIdList = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const value of raw) {
    const id = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
    if (!id) continue;
    const key = id.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    ids.push(id);
  }
  return ids;
};

export const hasDownloadAudienceFilters = (
  item: Pick<StoreDownloadItem, 'priceListIds' | 'zoneIds' | 'sellerIds'>
): boolean => item.priceListIds.length > 0 || item.zoneIds.length > 0 || item.sellerIds.length > 0;

export const downloadRequiresAuth = (
  item: Pick<StoreDownloadItem, 'requireAuth' | 'priceListIds' | 'zoneIds' | 'sellerIds'>
): boolean => item.requireAuth || hasDownloadAudienceFilters(item);

export const downloadAccessFromItem = (
  item: Pick<StoreDownloadItem, 'requireAuth' | 'priceListIds' | 'zoneIds' | 'sellerIds'>
): DownloadAccess => (downloadRequiresAuth(item) ? 'loggedIn' : 'public');

export const downloadAccessFromRequireAuth = (requireAuth: boolean): DownloadAccess =>
  requireAuth ? 'loggedIn' : 'public';

export const requireAuthFromDownloadAccess = (access: DownloadAccess): boolean => access === 'loggedIn';

const matchesDimension = (allowed: string[], actual?: string | null): boolean => {
  if (allowed.length === 0) return true;
  if (!actual || !actual.trim()) return false;
  const value = actual.trim().toLowerCase();
  return allowed.some((id) => id.trim().toLowerCase() === value);
};

export const matchesDownloadAudience = (
  item: Pick<StoreDownloadItem, 'priceListIds' | 'zoneIds' | 'sellerIds'>,
  audience?: DownloadCustomerAudience | null
): boolean =>
  matchesDimension(item.priceListIds, audience?.priceListId) &&
  matchesDimension(item.zoneIds, audience?.zoneId) &&
  matchesDimension(item.sellerIds, audience?.sellerId);

export const isDownloadVisibleToVisitor = (
  item: StoreDownloadItem,
  opts: {
    mode: 'editor' | 'live';
    isAuthenticated: boolean;
    audience?: DownloadCustomerAudience | null;
  }
): boolean => {
  if (opts.mode === 'editor') return true;
  if (!hasDownloadAudienceFilters(item)) return true;
  if (!opts.isAuthenticated) return false;
  return matchesDownloadAudience(item, opts.audience);
};

export const downloadAccessLabel = (
  item: Pick<StoreDownloadItem, 'requireAuth' | 'priceListIds' | 'zoneIds' | 'sellerIds'>,
  mode: 'editor' | 'live'
): string => {
  if (!downloadRequiresAuth(item)) return 'Acceso público';
  if (hasDownloadAudienceFilters(item)) {
    return mode === 'editor' ? 'Audiencia específica' : 'Disponible para tu cuenta';
  }
  return 'Solo usuarios logueados';
};

export const createDownloadAssetKey = (): string => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return id.toLowerCase();
};

export const normalizeDownloadAssetKey = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  if (/^download-[a-z0-9]{4,32}$/.test(trimmed)) return trimmed.slice('download-'.length);
  return /^[a-z0-9]{4,32}$/.test(trimmed) ? trimmed : undefined;
};

export const downloadKind = (assetKey: string): `download-${string}` => `download-${assetKey}`;

export const parseDownloadItem = (raw: unknown): StoreDownloadItem | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const data = raw as Record<string, unknown>;
  const assetKey = normalizeDownloadAssetKey(data.assetKey);
  if (!assetKey) return null;
  const id = typeof data.id === 'string' && data.id.trim() ? data.id.trim() : assetKey;
  const priceListIds = parseDownloadIdList(data.priceListIds);
  const zoneIds = parseDownloadIdList(data.zoneIds);
  const sellerIds = parseDownloadIdList(data.sellerIds);
  const hasFilters = priceListIds.length > 0 || zoneIds.length > 0 || sellerIds.length > 0;
  const fileName = typeof data.fileName === 'string' ? data.fileName : '';
  const url = normalizeDownloadUrl(data.url ?? data.externalUrl);
  return {
    id,
    title: typeof data.title === 'string' ? data.title : '',
    description: typeof data.description === 'string' ? data.description : '',
    assetKey,
    source: parseDownloadSource(data.source, url, fileName),
    fileName,
    fileSize: typeof data.fileSize === 'number' && Number.isFinite(data.fileSize) ? data.fileSize : 0,
    url,
    requireAuth: data.requireAuth === true || hasFilters,
    priceListIds,
    zoneIds,
    sellerIds,
  };
};

export const parseDownloadItems = (raw: unknown): StoreDownloadItem[] =>
  Array.isArray(raw)
    ? raw.map(parseDownloadItem).filter((item): item is StoreDownloadItem => Boolean(item))
    : [];

export const createEmptyDownloadItem = (): StoreDownloadItem => {
  const assetKey = createDownloadAssetKey();
  return {
    id: assetKey,
    title: '',
    description: '',
    assetKey,
    source: 'file',
    fileName: '',
    fileSize: 0,
    url: '',
    requireAuth: false,
    priceListIds: [],
    zoneIds: [],
    sellerIds: [],
  };
};

export const fileExtension = (fileName: string): string => {
  const match = /\.([a-z0-9]+)$/i.exec(fileName.trim());
  return match ? match[1].toLowerCase() : '';
};

export const normalizeDownloadUrl = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 2048) return '';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return trimmed;
  } catch {
    return '';
  }
};

export const parseDownloadSource = (
  raw: unknown,
  url: string,
  fileName: string
): StoreDownloadSource => {
  if (raw === 'link' || raw === 'file') return raw;
  return url && !fileName.trim() ? 'link' : 'file';
};

export const isDownloadLink = (
  item: Pick<StoreDownloadItem, 'source' | 'url' | 'fileName'>
): boolean => item.source === 'link' || (!!item.url && !item.fileName.trim());

export const hasDownloadContent = (
  item: Pick<StoreDownloadItem, 'source' | 'url' | 'fileName'>,
  mode: 'editor' | 'live'
): boolean => {
  if (mode === 'editor') return true;
  if (isDownloadLink(item)) return true;
  return Boolean(item.fileName.trim());
};

export const downloadLinkHost = (url: string): string => {
  const normalized = normalizeDownloadUrl(url);
  if (!normalized) return '';
  try {
    return new URL(normalized).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

export const openExternalDownloadUrl = (url: string): void => {
  const normalized = normalizeDownloadUrl(url);
  if (!normalized) return;
  const opened = window.open(normalized, '_blank', 'noopener,noreferrer');
  if (opened) return;
  const link = document.createElement('a');
  link.href = normalized;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};
